import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import arrowWhiteIcon from 'assets/arrow-white.svg';
import helpButtonIcon from 'assets/helpButton.svg';
import stopIcon from 'assets/stop-icon.svg';

import { ToolTip } from 'Components/Tooltip';

import { AskAnything } from 'Pages/TeamDashboard/CreateReview/components/AskAnything';
import askAnythingStyles from 'Pages/TeamDashboard/CreateReview/components/AskAnything/styles.module.scss';
import { CategoryFilter } from 'Pages/TeamDashboard/CreateReview/components/CategoryFilter';
import { ASK_ANYTHING_TABS } from 'Pages/TeamDashboard/CreateReview/components/constants';
import { DisclaimerFooter } from 'Pages/TeamDashboard/CreateReview/components/DisclaimerFooter';
import { FeedbackBox } from 'Pages/TeamDashboard/CreateReview/components/FeedbackBox';
import panelStyles from 'Pages/TeamDashboard/CreateReview/components/styles.module.scss';

import { getConversationMessages, listConversations } from 'services/conversationService';

import { FEEDBACK_EDGE_CASES, TEMPLATE_TITLES } from 'utlis/constants';

import styles from './styles.module.scss';

const INTERSECTION_THRESHOLD_COUNT = 21;

function isFeedbackInspectable(item, totalImages) {
  if (!item?.page) {
    return false;
  }

  if (totalImages <= 0) {
    return true;
  }

  const pageIndex = item.page - 1;

  return pageIndex >= 0 && pageIndex < totalImages;
}

export const FeedbackList = ({
  feedback,
  categoryName,
  watchDemo,
  isPublicView = false,
  onCategoryClick,
  onInspectFeedback,
  totalImages = 0,
  userClickSupersedesScrollRef,
  selectedFeedbackIndex = null,
}) => {
  const currentRecord = useSelector(state => state.historyReducer.currentRecord);

  const [showPopUp, setShowPopUp] = useState(false);
  const [activeTab, setActiveTab] = useState(ASK_ANYTHING_TABS.FINDINGS);
  const [askInputValue, setAskInputValue] = useState('');
  const [isInputHovering, setIsInputHovering] = useState(false);
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatHasMore, setChatHasMore] = useState(false);
  const [chatNextBeforeSeq, setChatNextBeforeSeq] = useState(null);
  const [isConversationLoaded, setIsConversationLoaded] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [isChatThinking, setIsChatThinking] = useState(false);

  const listContainerRef = useRef(null);
  const cardRefsRef = useRef([]);
  const dominantIndexRef = useRef(null);
  const visibleAreaByIndexRef = useRef({});
  const observerRef = useRef(null);
  const internalClickSupersedesScrollRef = useRef(false);
  const clickSupersedesScrollRef = userClickSupersedesScrollRef ?? internalClickSupersedesScrollRef;
  const programmaticScrollRef = useRef(false);
  const sendMessageRef = useRef(null);
  const pendingSendRef = useRef(false);

  const reviewType = currentRecord?.extra_data?.template ?? currentRecord?.template;
  const isGrowthAudit = reviewType === TEMPLATE_TITLES.GROWTH_AUDIT;
  const hasExistingChat = chatMessages.length > 0;

  // Load conversation data on mount for Growth Audit
  useEffect(() => {
    if (!isGrowthAudit || !currentRecord?.uuid || isConversationLoaded) {
      return;
    }

    const loadConversation = async () => {
      try {
        const response = await listConversations(currentRecord.uuid);

        if (response.results && response.results.length > 0) {
          const conversation = response.results[0];

          setConversationId(conversation.uuid);

          if (conversation.message_count > 0) {
            const messagesResponse = await getConversationMessages(conversation.uuid);

            setChatMessages(messagesResponse.messages.map(msg => ({
              id: msg.uuid,
              role: msg.role,
              content: msg.content,
              sequence: msg.sequence,
              created_at: msg.created_at,
            })));
            setChatHasMore(messagesResponse.has_more);
            setChatNextBeforeSeq(messagesResponse.next_before_sequence);
          }
        }
      }
      catch {
        // Silently fail
      }
      finally {
        setIsConversationLoaded(true);
      }
    };

    loadConversation();
  }, [isGrowthAudit, currentRecord?.uuid, isConversationLoaded]);

  const handleCategorySelect = categoryKey => {
    if (onCategoryClick) {
      // If parent component handles category navigation, use that
      onCategoryClick(categoryKey);
    }
  };

  const handleInspectFromUserClick = useCallback(
    feedbackData => {
      onInspectFeedback?.(feedbackData);
    },
    [onInspectFeedback]
  );

  const handleSendMessage = useCallback(() => {
    if (!askInputValue.trim()) {
      return;
    }

    if (activeTab !== ASK_ANYTHING_TABS.ASK_AUDIT) {
      pendingSendRef.current = true;
      setActiveTab(ASK_ANYTHING_TABS.ASK_AUDIT);

      return;
    }

    if (sendMessageRef.current) {
      sendMessageRef.current();
    }
  }, [askInputValue, activeTab]);

  const handleInputKeyDown = useCallback(e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  useEffect(() => {
    if (activeTab === ASK_ANYTHING_TABS.ASK_AUDIT && pendingSendRef.current) {
      pendingSendRef.current = false;

      const timer = setTimeout(() => {
        if (sendMessageRef.current) {
          sendMessageRef.current();
        }
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [activeTab]);

  useEffect(() => {
    if (showPopUp) {
      const timer = setTimeout(() => {
        setShowPopUp(false);
      }, 5000); // Hide after 5 seconds

      // Cleanup the timer on component unmount or when showPopUp changes
      return () => clearTimeout(timer);
    }
  }, [showPopUp]);

  const feedbackIndexMap = useMemo(() => {
    const map = new Map();

    currentRecord.final_feedback.forEach((feedbackItem, index) => {
      map.set(feedbackItem, index);
    });

    return map;
  }, [currentRecord]);

  const errorCardValues = useMemo(() => {
    if (!feedback.length) {
      return FEEDBACK_EDGE_CASES.NO_FEEDBACK;
    }
    if (currentRecord?.extra_data?.page_type === null) {
      return FEEDBACK_EDGE_CASES.NOT_SUPPORTED;
    }

    return null;
  }, [currentRecord]);

  // Count dismissed feedbacks for public view
  const dismissedCount = useMemo(() => {
    if (!isPublicView) {
      return 0;
    }

    return feedback.filter(val => val.dismiss === true).length;
  }, [feedback, isPublicView]);

  const updateDominantCard = useCallback(
    (feedbackList, onInspect, imagesCount) => {
      if (clickSupersedesScrollRef.current) {
        return;
      }

      if (!feedbackList.length) {
        return;
      }

      const allIndices = [...feedbackList.keys()];
      const dominantIndex = allIndices.reduce((best, i) => {
        const area = visibleAreaByIndexRef.current[i] || 0;
        const bestArea = visibleAreaByIndexRef.current[best] || 0;

        return area > bestArea ? i : best;
      }, allIndices[0]);

      if (!isFeedbackInspectable(feedbackList[dominantIndex], imagesCount)) {
        return;
      }

      if (dominantIndex != null && dominantIndex !== dominantIndexRef.current && onInspect) {
        dominantIndexRef.current = dominantIndex;
        onInspect(feedbackList[dominantIndex]);
      }
    },
    []
  );

  useEffect(() => {
    const container = listContainerRef.current;

    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      if (programmaticScrollRef.current) {
        return;
      }

      clickSupersedesScrollRef.current = false;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [clickSupersedesScrollRef]);

  // Scroll the selected feedback card into view when selection comes from pin click or card click.
  useEffect(() => {
    if (
      selectedFeedbackIndex != null
      && selectedFeedbackIndex >= 0
      && selectedFeedbackIndex < feedback.length
      && cardRefsRef.current[selectedFeedbackIndex]
    ) {
      programmaticScrollRef.current = true;
      cardRefsRef.current[selectedFeedbackIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      const t = setTimeout(() => {
        programmaticScrollRef.current = false;
      }, 800);

      return () => clearTimeout(t);
    }
  }, [selectedFeedbackIndex, feedback.length]);

  useEffect(() => {
    if (!onInspectFeedback || !feedback.length || !listContainerRef.current) {
      return undefined;
    }

    dominantIndexRef.current = null;
    visibleAreaByIndexRef.current = {};
    cardRefsRef.current = cardRefsRef.current.slice(0, feedback.length);

    const container = listContainerRef.current;
    const thresholds = Array.from(
      { length: INTERSECTION_THRESHOLD_COUNT },
      // eslint-disable-next-line no-unused-vars -- index is used; first param is array element (none)
      (_, i) => i / (INTERSECTION_THRESHOLD_COUNT - 1)
    );

    // eslint-disable-next-line compat/compat -- IntersectionObserver used for scroll-dominant card; not targeting IE11
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const index = parseInt(entry.target.getAttribute('data-feedback-index'), 10);

          if (Number.isNaN(index) || index < 0 || index >= feedback.length) {
            return;
          }

          const area = entry.intersectionRect.width * entry.intersectionRect.height;

          visibleAreaByIndexRef.current[index] = area;
        });
        updateDominantCard(feedback, onInspectFeedback, totalImages);
      },
      {
        root: container,
        rootMargin: '0px',
        threshold: thresholds,
      }
    );

    observerRef.current = observer;

    const cardElements = cardRefsRef.current;

    cardElements.forEach(el => {
      if (el) {
        observer.observe(el);
      }
    });

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [feedback, onInspectFeedback, totalImages, updateDominantCard]);

  const renderFindingsContent = () => {
    if (errorCardValues) {
      return (
        <div className={styles['error-container']}>
          <img className={styles['image']} src={errorCardValues.image} alt="a problem occurred" />
          <div className={styles['text-container']}>
            <h2 className={styles['title']}>
              {errorCardValues.title}
            </h2>
            <p className={styles['sub-title']}>
              {errorCardValues.description}
            </p>
            <div className={styles['bottom-container']}>
              {errorCardValues === FEEDBACK_EDGE_CASES.NOT_SUPPORTED && (
                <button className={styles['button']} onClick={watchDemo}>
                  Watch demo
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <>
        {!isGrowthAudit && (
          <CategoryFilter
            categoryName={categoryName}
            onCategorySelect={handleCategorySelect}
          />
        )}
        <div ref={listContainerRef} className={cn(styles['feedback-container'])}>
          {feedback.map((val, ind) => (
            <div
              key={`${categoryName}-${ind}-${val.issue || val.detailed_feedback?.[0] || ind}`}
              ref={el => {
                cardRefsRef.current[ind] = el;
              }}
              data-feedback-index={ind}
            >
              <FeedbackBox
                data={val}
                index={ind}
                currentFeedback={
                  val.index !== undefined
                    ? val.index + 1
                    : feedbackIndexMap.get(val) + 1
                }
                selectedCategory={categoryName}
                selectedLabelCategory="All themes"
                isPublicView={isPublicView}
                onInspectFeedback={handleInspectFromUserClick}
                totalImages={totalImages}
                isSelected={selectedFeedbackIndex === ind}
              />
            </div>
          ))}
          {isPublicView && dismissedCount > 0 && (
            <div className="flex items-center gap-8 py-2 mb-24">
              <span className="font-normal text-sm leading-5 text-left" style={{ color: '#8F97A5' }}>
                {dismissedCount}
                {' '}
                {dismissedCount === 1 ? 'finding has' : 'findings have'}
                {' '}
                been hidden
              </span>
              <ToolTip
                title="Audit authors can hide findings based on relevance or accuracy"
                position="right"
                width="150px"
              >
                <img
                  src={helpButtonIcon}
                  alt="Help"
                  className="cursor-pointer"
                  style={{ width: '16px', height: '16px' }}
                />
              </ToolTip>
            </div>
          )}
        </div>
      </>
    );
  };

  const showAskAnythingInput = isGrowthAudit && !isPublicView;

  return (
    <div
      className={cn(
        'mx-8',
        panelStyles.createReviewRightPanel,
        styles['feedback-page'],
        { [styles['feedback-container-with-footer']]: !showAskAnythingInput },
        { [styles['feedback-container-with-input']]: showAskAnythingInput }
      )}
    >
      {isGrowthAudit && (
        <div className={askAnythingStyles['tabs-header']}>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={cn(
                askAnythingStyles.tab,
                { [askAnythingStyles['tab--active']]: activeTab === ASK_ANYTHING_TABS.FINDINGS }
              )}
              onClick={() => setActiveTab(ASK_ANYTHING_TABS.FINDINGS)}
            >
              Findings
            </button>
            {!isPublicView && (
              <button
                type="button"
                className={cn(
                  askAnythingStyles.tab,
                  { [askAnythingStyles['tab--active']]: activeTab === ASK_ANYTHING_TABS.ASK_AUDIT }
                )}
                onClick={() => setActiveTab(ASK_ANYTHING_TABS.ASK_AUDIT)}
              >
                Ask audit
              </button>
            )}
          </div>
          {activeTab === ASK_ANYTHING_TABS.FINDINGS && (
            <CategoryFilter
              categoryName={categoryName}
              onCategorySelect={handleCategorySelect}
              compact
            />
          )}
        </div>
      )}

      {activeTab === ASK_ANYTHING_TABS.FINDINGS && renderFindingsContent()}

      {activeTab === ASK_ANYTHING_TABS.ASK_AUDIT && isGrowthAudit && (
        <AskAnything
          onSendMessage={sendMessageRef}
          inputValue={askInputValue}
          setInputValue={setAskInputValue}
          isHovering={isInputHovering}
          setIsHovering={setIsInputHovering}
          onThinkingChange={setIsAiThinking}
          designReviewUuid={currentRecord?.uuid}
          conversationId={conversationId}
          setConversationId={setConversationId}
          chatMessages={chatMessages}
          setChatMessages={setChatMessages}
          chatHasMore={chatHasMore}
          setChatHasMore={setChatHasMore}
          chatNextBeforeSeq={chatNextBeforeSeq}
          setChatNextBeforeSeq={setChatNextBeforeSeq}
          streamingContent={streamingContent}
          setStreamingContent={setStreamingContent}
          isChatThinking={isChatThinking}
          setIsChatThinking={setIsChatThinking}
        />
      )}

      {/* Footer: Input for Growth Audit, Disclaimer for others */}
      {showAskAnythingInput
        ? (
          <div className={askAnythingStyles['input-footer']}>
            <div
              className={cn(
                askAnythingStyles['input-wrapper'],
                { [askAnythingStyles['input-wrapper--has-value']]: askInputValue.length > 0 }
              )}
            >
              <textarea
                className={cn(
                  askAnythingStyles['input-field'],
                  { [askAnythingStyles['input-field--preview']]: isInputHovering }
                )}
                placeholder={!isConversationLoaded ? '' : (hasExistingChat ? 'Continue chat' : 'Ask anything')}
                value={askInputValue}
                onChange={e => {
                  setIsInputHovering(false);
                  setAskInputValue(e.target.value);
                }}
                onKeyDown={handleInputKeyDown}
                rows={2}
              />
              <button
                type="button"
                className={cn(
                  askAnythingStyles['send-button'],
                  {
                    [askAnythingStyles['send-button--active']]:
                      askInputValue.trim().length > 0 && !isInputHovering && !isAiThinking,
                  },
                  { [askAnythingStyles['send-button--stop']]: isAiThinking }
                )}
                onClick={handleSendMessage}
                disabled={(!askInputValue.trim() || isInputHovering) && !isAiThinking}
              >
                <img
                  src={isAiThinking ? stopIcon : arrowWhiteIcon}
                  alt={isAiThinking ? 'Stop' : 'Send'}
                  className={askAnythingStyles['send-icon']}
                />
              </button>
            </div>
          </div>
        )
        : (
          <DisclaimerFooter />
        )}
    </div>
  );
};

FeedbackList.propTypes = {
  feedback: PropTypes.array.isRequired,
  categoryName: PropTypes.string.isRequired,
  watchDemo: PropTypes.func.isRequired,
  isPublicView: PropTypes.bool,
  onCategoryClick: PropTypes.func,
  onInspectFeedback: PropTypes.func,
  totalImages: PropTypes.number,
  userClickSupersedesScrollRef: PropTypes.shape({ current: PropTypes.bool }),
  selectedFeedbackIndex: PropTypes.number,
};
